/**
 * Orders Table Component
 * Main orders table with sorting, column resizing, and role-based cell rendering
 * Extracted from OrdersManagement.jsx
 */
import React from 'react';
import { Inbox, AlertTriangle } from 'lucide-react';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { FaxIndicator } from '../FaxIndicator';
import { EmailIndicator } from '../EmailIndicator';
import ClarificationSentIndicator from '../ClarificationSentIndicator';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { ResizableTableHead, ResizeGuide } from '../../ResizableTable';
import { StatusBadge, normalizeStatusForOrderDesk } from '../../StatusBadge';
import MlsDualStatusBadges from '../../mls/MlsDualStatusBadges';
import { nRole } from '../../../utils/roleNormalizer';
import { formatDateOnly, formatDateTimeET } from '../../../utils/formatters';
import { ProductWithCode, productDisplayText } from '../ProductWithCode';

/**
 * MLS-OWNERSHIP-UI (Aug 23, 2026, rev 2 — READ-ONLY) — one compact ownership
 * line for a dual MLS component: small TAX/MLS identifier label + processor
 * name, or subtle read-only "Unassigned". The column answers "who owns this
 * work?" only; assignment happens exclusively via the order-list assignment
 * controls / Fulfillment component controls. Ownership source is strictly
 * components.{tax|mls}.assignee_name — never order.assignee_id.
 */
const MlsComponentLine = ({ orderId, comp, name }) => {
  const isTax = comp === 'tax';
  const labelCls = isTax
    ? 'bg-blue-50 text-blue-700 border-blue-200'
    : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return (
    <div className="flex items-center gap-1.5 leading-tight min-w-0">
      <span
        className={`shrink-0 inline-flex items-center justify-center w-[30px] rounded-sm border px-1 text-[9px] font-bold tracking-wider ${labelCls}`}
        data-testid={`mls-comp-label-${comp}-${orderId}`}
      >
        {isTax ? 'TAX' : 'MLS'}
      </span>
      {name ? (
        <span className="truncate text-gray-800" title={name} data-testid={`mls-comp-name-${comp}-${orderId}`}>
          {name}
        </span>
      ) : (
        <span className="italic text-gray-400" data-testid={`mls-comp-unassigned-${comp}-${orderId}`}>
          Unassigned
        </span>
      )}
    </div>
  );
};

/**
 * Table Styles Component
 * Encapsulates CSS-in-JS styles for the orders table
 */
const TableStyles = () => (
  <style>{`
    .orders-table {
      table-layout: fixed;
      border-collapse: separate;
      border-spacing: 0;
      width: 100%;
      max-width: 100%;
    }
    .orders-table thead th {
      box-sizing: border-box;
      position: sticky;
      top: 0;
      z-index: 10;
    }
    .orders-table tbody tr {
      border-bottom: 1px solid #e5e7eb !important;
    }
    .orders-table tbody td {
      box-sizing: border-box;
      padding-top: 12px;
      padding-bottom: 12px;
    }
    .actions-cell {
      padding-right: 8px !important;
      white-space: nowrap;
      overflow: hidden;
    }
    .actions-cell button {
      flex-shrink: 0;
    }
    .table-cell-truncate {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .property-cell {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1.4;
    }
    .property-cell-wrap {
      white-space: normal;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      line-height: 1.4;
      max-height: 2.8em;
      word-break: break-word;
    }
    .borrower-cell {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .priority-badge {
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
    }
    @media (max-width: 1280px) {
      .orders-table {
        font-size: 0.875rem;
      }
    }
  `}</style>
);

/**
 * Get status badge variant based on status value
 */
const getStatusBadgeVariant = (status) => {
  switch (status) {
    case 'created': return 'secondary';
    case 'open': return 'default';
    case 'pending': return 'outline';
    case 'clarification': return 'destructive';
    case 'mailaway': return 'outline';
    case 'mail_away': return 'outline';
    case 'mail_away_received': return 'default';
    case 'complete': return 'default';
    case 'processing': return 'default';
    case 'completed': return 'default';
    case 'cancelled': return 'secondary';
    default: return 'secondary';
  }
};

/**
 * Table Header Checkbox
 * Renders checkbox in table header for bulk selection
 */
const TableHeaderCheckbox = ({ 
  column, 
  user, 
  orders, 
  selectedOrders, 
  selectedOrderIds, 
  onAdminSelectAll, 
  onSelectAll 
}) => {
  const colWidth = parseInt(column.width) || 40;
  
  // Admin checkbox
  if (user?.role === 'admin') {
    return (
      <TableHead 
        key={column.id}
        style={{ 
          ['--col-w']: `${colWidth}px`,
          width: column.width, 
          minWidth: column.minWidth, 
          maxWidth: column.maxWidth 
        }}
      >
        <input
          type="checkbox"
          checked={orders.length > 0 && orders.every(o => selectedOrders.has(o.id))}
          onChange={(e) => onAdminSelectAll(e.target.checked)}
          className="cursor-pointer"
          data-testid="select-all-checkbox"
        />
      </TableHead>
    );
  }
  
  // Team Leader checkbox
  if (['title_search_team_leader', 'tax_search_team_leader', 'team_leader'].includes(nRole(user?.role))) {
    const allSelected = orders.length > 0 && orders.every(o => selectedOrderIds.has(o.id));
    const someSelected = selectedOrderIds.size > 0 && !allSelected;
    
    return (
      <TableHead 
        key={column.id}
        className="text-center"
        style={{ 
          ['--col-w']: `${colWidth}px`,
          width: column.width,
          minWidth: column.minWidth || column.width,
          maxWidth: column.maxWidth || column.width
        }}
      >
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(input) => {
              if (input) input.indeterminate = someSelected;
            }}
            onChange={(e) => onSelectAll(e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 cursor-pointer"
            title={allSelected ? "Unselect all orders" : "Select all orders"}
            data-testid="select-all-checkbox"
          />
        </div>
      </TableHead>
    );
  }
  
  return null;
};

/**
 * Table Cell Content Renderer
 * Renders cell content based on column type and user role
 */
const renderCellContent = ({
  columnId,
  column,
  order,
  user,
  selectedOrders,
  selectedOrderIds,
  availableProcessors,
  onAdminSelectOrder,
  onSelectOrder,
  onOrderNumberClick,
  canOpenOrderViaMatrix = false,
  isOrderDeskView = false,
  inMlsContext = false,
  getCellDensityClasses,
  formatPropertyAddress,
  getBorrowerName,
  getPropertyCounty,
  getPropertyState
}) => {
  const colWidth = parseInt(column?.width) || 120;
  
  switch (columnId) {
    case 'checkbox':
      // Checkbox for bulk selection (Admin and Team Leaders)
      if (user?.role === 'admin') {
        return (
          <TableCell 
            style={{ 
              ['--col-w']: `${colWidth}px`,
              width: column.width,
              minWidth: column.minWidth || column.width,
              maxWidth: column.maxWidth || column.width
            }}
            className="text-center"
            data-col="checkbox"
          >
            <input
              type="checkbox"
              checked={selectedOrders.has(order.id)}
              onChange={(e) => onAdminSelectOrder(order.id, e.target.checked)}
              className="cursor-pointer"
              onClick={(e) => e.stopPropagation()}
              data-testid={`select-order-${order.id}`}
            />
          </TableCell>
        );
      } else if (['title_search_team_leader', 'tax_search_team_leader', 'team_leader'].includes(nRole(user?.role))) {
        const isSelected = selectedOrderIds.has(order.id);
        return (
          <TableCell 
            className={`${getCellDensityClasses()} text-center`}
            style={{ 
              width: column.width,
              minWidth: column.minWidth || column.width,
              maxWidth: column.maxWidth || column.width
            }}
            data-col="checkbox"
          >
            <div className="flex items-center justify-center">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={(e) => onSelectOrder(order.id, e.target.checked)}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 cursor-pointer"
                title={isSelected ? "Unselect this order" : "Select this order"}
                onClick={(e) => e.stopPropagation()}
                data-testid={`select-order-${order.id}`}
              />
            </div>
          </TableCell>
        );
      }
      return null;
      
    case 'order_number':
      const isTeamLeader = ['title_search_team_leader', 'tax_search_team_leader', 'team_leader'].includes(nRole(user?.role));
      const isAdmin = nRole(user?.role) === 'admin';
      const isOrderDesk = nRole(user?.role) === 'order_desk';
      // RBAC-PILOT-ORDERS-001: matrix orders:view grant also opens the Order Summary popup
      const isClickable = isTeamLeader || isAdmin || isOrderDesk || canOpenOrderViaMatrix;
      
      return (
        <TableCell 
          className={`font-medium whitespace-nowrap ${getCellDensityClasses()}`} 
          title={order.order_number}
          style={{ 
            width: column.width,
            minWidth: column.minWidth || column.width,
            maxWidth: column.maxWidth || column.width,
            overflow: 'visible'
          }}
        >
          {isClickable ? (
            <button
              onClick={() => onOrderNumberClick(order)}
              className={`hover:text-blue-800 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 rounded ${['rush', 'urgent'].includes(order.priority?.toLowerCase()) ? 'text-red-600 font-bold' : 'text-blue-600'}`}
              data-testid={`order-number-${order.order_number}`}
            >
              {order.order_number}
            </button>
          ) : (
            <span className={['rush', 'urgent'].includes(order.priority?.toLowerCase()) ? 'text-red-600 font-bold' : ''}>
              {order.order_number}
            </span>
          )}
        </TableCell>
      );
      
    case 'order_date': {
      // Order Date column — Feb 25, 2026 (P1).
      // Sources from order.created_at (NOT order.order_date — that field
      // is a Pydantic auto-generated default that fires on each API call).
      // Format: "Apr 12, 2026" in the account timezone (ET).
      const rawDate = order.created_at || order.received_date;
      let formattedDate = '—';
      if (rawDate) {
        try {
          formattedDate = formatDateOnly(rawDate);
        } catch { formattedDate = '—'; }
      }
      return (
        <TableCell
          className={`whitespace-nowrap tabular-nums ${getCellDensityClasses()}`}
          title={rawDate ? formatDateTimeET(rawDate) : ''}
          style={{
            width: column.width,
            minWidth: column.minWidth || column.width,
            maxWidth: column.maxWidth || column.width,
          }}
          data-testid={`order-date-${order.id}`}
        >
          <span className={!rawDate ? 'text-slate-400' : 'text-slate-700'}>{formattedDate}</span>
        </TableCell>
      );
    }
      
    case 'client_code':
      const clientShortCode = order.client_short_code || order.client_code?.substring(0, 4) || 'N/A';
      const clientFullCode = order.client_code || 'N/A';
      
      return (
        <TableCell 
          className={getCellDensityClasses()}
          style={{ 
            width: column.width,
            minWidth: column.minWidth || column.width,
            maxWidth: column.maxWidth || column.width
          }}
          title={`Full Code: ${clientFullCode}`}
        >
          <Badge variant="outline" className="text-xs font-mono">{clientShortCode}</Badge>
        </TableCell>
      );
      
    case 'client_product':
      {
        const product = order.client_product || 'N/A';
        const contract = order.contract_code;  // bulk-upload or manual (CONTRACT-CODE-MANUAL-001)
        return (
          <TableCell
            className={`col--product overflow-hidden ${getCellDensityClasses()}`}
            title={productDisplayText(product, contract)}
            data-testid={`order-row-product-${order.id}`}
            style={{
              width: column.width,
              minWidth: column.minWidth || column.width,
              maxWidth: column.maxWidth || column.width
            }}
          >
            <ProductWithCode
              product={product}
              code={contract}
              codeTestId={`order-row-contract-code-${order.id}`}
              className="w-full"
            />
          </TableCell>
        );
      }
      
    case 'customer':
      return (
        <TableCell 
          className={`col--borrower borrower-cell ${getCellDensityClasses()}`} 
          title={order.seller_name ? `${getBorrowerName(order)} · Seller: ${order.seller_name}` : getBorrowerName(order)}
        >
          {getBorrowerName(order)}
          {/* SELLER-NAME-001: sub-line only when a seller was captured */}
          {order.seller_name && (
            <div className="text-[11px] leading-tight text-slate-500 truncate" data-testid={`order-seller-${order.id}`}>
              Seller: {order.seller_name}
            </div>
          )}
        </TableCell>
      );
      
    case 'property':
      return (
        <TableCell 
          className={`col--address property-cell ${getCellDensityClasses()}`} 
          title={formatPropertyAddress(order)}
          style={{
            paddingLeft: '8px',
            paddingRight: '8px'
          }}
        >
          {formatPropertyAddress(order)}
        </TableCell>
      );
      
    case 'priority':
      return (
        <TableCell 
          className={getCellDensityClasses()}
          style={{ 
            width: column.width,
            minWidth: column.minWidth || column.width,
            maxWidth: column.maxWidth || column.width
          }}
        >
          <Badge 
            variant={order.priority === 'urgent' ? 'destructive' : order.priority === 'rush' ? 'default' : 'secondary'} 
            className="priority-badge text-xs"
          >
            {order.priority}
          </Badge>
        </TableCell>
      );
      
    case 'county':
      return (
        <TableCell 
          className={`table-cell-truncate ${getCellDensityClasses()}`} 
          title={getPropertyCounty(order)}
          style={{ 
            width: column.width,
            minWidth: column.minWidth || column.width,
            maxWidth: column.maxWidth || column.width
          }}
        >
          {getPropertyCounty(order)}
        </TableCell>
      );
      
    case 'state':
      return (
        <TableCell 
          className={`col--state table-cell-truncate ${getCellDensityClasses()}`} 
          title={getPropertyState(order)}
          style={{ 
            width: column.width,
            minWidth: column.minWidth || column.width,
            maxWidth: column.maxWidth || column.width
          }}
        >
          {getPropertyState(order)}
        </TableCell>
      );
      
    case 'status':
      return (
        <TableCell 
          className={`col--status ${getCellDensityClasses()}`}
          style={{ 
            width: column.width,
            minWidth: column.minWidth || column.width,
            maxWidth: column.maxWidth || column.width
          }}
        >
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              {order.mls_status ? (
                /* MLS-PROC-SCOPE-001 (A): a valid dual order always shows its component
                   statuses; the tooltip carries the order-level status the tabs use. */
                <MlsDualStatusBadges order={order} showOrderLevelHint={!inMlsContext} />
              ) : (
                <StatusBadge status={order.status} disableHover={isOrderDeskView} userRole={user?.role} orderData={order} />
              )}
              <FaxIndicator faxStatus={order.fax_status} faxDeliveries={order.fax_deliveries} />
              <EmailIndicator taxEmailStatus={order.tax_email_status} taxEmailRequests={order.tax_email_requests} />
              <ClarificationSentIndicator order={order} />
              {isOrderDeskView && order.status === 'created' && (
                <span className="text-xs">✏️</span>
              )}
            </div>
          </div>
        </TableCell>
      );
      
    case 'assignee':
      {
        // MLS-OWNERSHIP-UI rev 2 (Aug 23, 2026): dual MLS orders show a
        // READ-ONLY compact two-line ownership block — TAX/MLS identifier
        // labels + processor names from components.{tax|mls}.assignee_name
        // only. Unassigned components show subtle read-only "Unassigned";
        // assignment happens exclusively via the bulk/Fulfillment controls.
        const dualComps = order.components;
        const isDualMlsRow = !!(dualComps && dualComps.tax && dualComps.mls);
        if (isDualMlsRow) {
          const taxName = dualComps.tax?.assignee_name || null;
          const mlsName = dualComps.mls?.assignee_name || null;
          return (
            <TableCell
              className={`col--assignee ${getCellDensityClasses()}`}
              data-col="assignee"
              data-testid={`order-row-assignee-${order.id}`}
              style={{
                width: column.width,
                minWidth: column.minWidth || column.width,
                maxWidth: column.maxWidth || column.width,
                textAlign: 'left',
                paddingTop: '4px',
                paddingBottom: '4px',
                verticalAlign: 'middle'
              }}
            >
              <div className="flex flex-col justify-center gap-[3px] text-[13px]">
                <MlsComponentLine orderId={order.id} comp="tax" name={taxName} />
                <MlsComponentLine orderId={order.id} comp="mls" name={mlsName} />
              </div>
            </TableCell>
          );
        }
        // CANONICAL DEFINITION of "unassigned" per Feb 21, 2026 fix —
        // single source of truth is `order.assignee_id`. The previous check
        // (falsy `assignee_name`) diverged from the backend's unassigned_only
        // filter and produced rows labeled "Unassigned" that the filter then
        // silently excluded.
        // TYPING-ASSIGNEE-001: in Typing the operational owner is the Typing Processor
        // (typing_assigned_to*). The Search Processor assignment stays untouched in data.
        const isTypingStage = order.status === 'typing';
        // QC-ASSIGNEE-001: in QC Review the operational owner is the QC claimant (qc_assigned_to*)
        // once claimed; unclaimed QC rows keep showing the Search Processor. Data untouched.
        const isQcClaimed = order.status === 'qc_review' && !!order.qc_assigned_to;
        const hasAssignee = isTypingStage ? !!order.typing_assigned_to : !!order.assignee_id;
        // Prefer the denormalized `assignee_name` for performance; fall back to
        // a neutral "Assigned" label if name is missing but the order IS
        // assigned (data-heal backfills the name over time). The legacy
        // `assignee_name === 'Unassigned'` string literal is no longer trusted.
        const displayName = isQcClaimed
          ? ((order.qc_assigned_to_name || '').trim() || 'QC Specialist')
          : isTypingStage
          ? (hasAssignee ? ((order.typing_assigned_to_name || '').trim() || 'Assigned') : 'Unassigned')
          : hasAssignee
            ? (order.assignee_name && order.assignee_name !== 'Unassigned' && order.assignee_name !== 'unassigned'
                ? order.assignee_name
                : 'Assigned')
            : 'Unassigned';
        return (
          <TableCell
            className={`col--assignee ${getCellDensityClasses()}`}
            data-col="assignee"
            data-testid={`order-row-assignee-${order.id}`}
            style={{
              width: column.width,
              minWidth: column.minWidth || column.width,
              maxWidth: column.maxWidth || column.width,
              textAlign: 'left',
              color: (hasAssignee || isQcClaimed) ? '#374151' : '#9ca3af',
              fontStyle: (hasAssignee || isQcClaimed) ? 'normal' : 'italic'
            }}
            title={isQcClaimed ? `Search Processor: ${order.assignee_name || (order.assignee_id ? 'Assigned' : 'Unassigned')}` : undefined}
          >
            {displayName}
            {isQcClaimed && <span className="ml-1.5 rounded bg-fuchsia-100 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-fuchsia-700">QC</span>}
          </TableCell>
        );
      }
      
    case 'actions':
      return (
        <TableCell 
          className={`col--actions actions-cell ${getCellDensityClasses()}`}
          style={{ 
            ['--col-w']: `${colWidth}px`,
            width: column.width,
            minWidth: column.width,
            maxWidth: column.width
          }}
          data-col="actions"
        >
          {isOrderDeskView && (
            <span className="text-xs text-gray-500">-</span>
          )}
        </TableCell>
      );
      
    case 'created_date': {
      const rawDate = order.created_at || order.received_date;
      let formattedDate = '-';
      if (rawDate) {
        try {
          formattedDate = formatDateOnly(rawDate);
        } catch { formattedDate = '-'; }
      }
      return (
        <TableCell 
          className={`table-cell-truncate ${getCellDensityClasses()}`}
          title={rawDate ? formatDateTimeET(rawDate) : ''}
          style={{ 
            width: column.width,
            minWidth: column.minWidth || column.width,
            maxWidth: column.maxWidth || column.width
          }}
          data-testid={`order-created-date-${order.id}`}
        >
          {formattedDate}
        </TableCell>
      );
    }

    case 'product_id':
      return (
        <TableCell 
          className={`table-cell-truncate ${getCellDensityClasses()}`} 
          title={order.product_id || 'N/A'}
          style={{ 
            width: column.width,
            minWidth: column.minWidth || column.width,
            maxWidth: column.maxWidth || column.width
          }}
        >
          {order.product_id || 'N/A'}
        </TableCell>
      );
      
    default:
      return null;
  }
};

/**
 * Pagination Controls Component
 */
export const PaginationControls = ({
  currentPage,
  totalOrders,
  ordersPerPage,
  ordersCount,
  loading,
  onPageChange
}) => {
  const totalPages = Math.ceil(totalOrders / ordersPerPage);
  
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPages = Math.min(5, totalPages);
    
    for (let i = 0; i < maxPages; i++) {
      let pageNum;
      
      if (totalPages <= 5) {
        pageNum = i + 1;
      } else if (currentPage <= 3) {
        pageNum = i + 1;
      } else if (currentPage >= totalPages - 2) {
        pageNum = totalPages - 4 + i;
      } else {
        pageNum = currentPage - 2 + i;
      }
      
      pages.push(pageNum);
    }
    
    return pages;
  };
  
  return (
    <div className="border-t px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600" data-testid="pagination-summary">
          {loading && totalOrders === 0 ? (
            // Avoid the misleading "0 to 0 of 0 orders" flash while the
            // initial fetch is in flight (Feb 2026 prod report).
            <span className="inline-flex items-center gap-2">
              <span className="inline-block h-2 w-2 rounded-full bg-slate-400 animate-pulse" />
              Loading orders…
            </span>
          ) : (
            <>
              Showing {ordersCount > 0 ? ((currentPage - 1) * ordersPerPage + 1) : 0} to{' '}
              {Math.min(currentPage * ordersPerPage, totalOrders)} of {totalOrders} orders
            </>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            data-testid="pagination-prev"
          >
            Previous
          </Button>
          
          <div className="flex items-center gap-1">
            {getPageNumbers().map((pageNum) => (
              <Button
                key={pageNum}
                variant={pageNum === currentPage ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(pageNum)}
                disabled={loading}
                className="w-8 h-8 p-0"
                data-testid={`pagination-page-${pageNum}`}
              >
                {pageNum}
              </Button>
            ))}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages || loading}
            data-testid="pagination-next"
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Orders Table Component
 * Main table component for displaying orders
 */
export const OrdersTable = ({
  // Data
  orders,
  visibleColumns,
  // Loading state — Feb 2026 fix:
  // When `loading=true && orders.length===0` (initial fetch / status-tab
  // change), render a skeleton row so users never see a phantom "0 orders"
  // table during the 200-2000ms it takes the v2 endpoint to respond on
  // production data volumes. Pagination text downstream also gates on this.
  loading = false,
  fetchError = false,
  onRetry,
  // SEARCH-PERF-001 — active search term (empty string when not searching);
  // drives the search-specific empty state below.
  searchTerm = '',
  // User & Role
  user,
  // Selection state
  selectedOrders,
  selectedOrderIds,
  // Processors for assignment display
  availableProcessors,
  // Table density
  tableDensity = 'normal',
  // Resize handlers
  tableRef,
  isResizing,
  resizingColumn,
  onResizeStart,
  onAutoFit,
  onKeyboardResize,
  // Selection handlers
  onAdminSelectAll,
  onAdminSelectOrder,
  onSelectAll,
  onSelectOrder,
  // Order click handler
  onOrderNumberClick,
  canOpenOrderViaMatrix = false,
  isOrderDeskView = false,
  inMlsContext = false,
  // Sorting props
  sortConfig = { key: null, direction: null },
  onSort,
  // Helper functions
  formatPropertyAddress,
  getBorrowerName,
  getPropertyCounty,
  getPropertyState
}) => {
  // Density helper functions
  const getDensityClasses = () => {
    switch (tableDensity) {
      case 'compact': return 'h-8';
      case 'comfortable': return 'h-14';
      default: return 'h-12';
    }
  };

  const getCellDensityClasses = () => {
    switch (tableDensity) {
      case 'compact': return 'py-1 px-2 text-sm';
      case 'comfortable': return 'py-4 px-4';
      default: return 'py-2 px-3';
    }
  };

  return (
    <>
      <TableStyles />
      <Table 
        ref={tableRef} 
        className={`orders-table ${user?.role === 'admin' ? 'table-admin-orders' : ''}`}
      >
        <colgroup>
          {visibleColumns.map((column) => (
            <col 
              key={column.id}
              style={{ width: column.width }}
              className={`col--${column.id}`}
            />
          ))}
        </colgroup>
        <TableHeader>
          <TableRow>
            {visibleColumns.map((column) => {
              // Checkbox column handling
              if (column.id === 'checkbox') {
                return (
                  <TableHeaderCheckbox
                    key={column.id}
                    column={column}
                    user={user}
                    orders={orders}
                    selectedOrders={selectedOrders}
                    selectedOrderIds={selectedOrderIds}
                    onAdminSelectAll={onAdminSelectAll}
                    onSelectAll={onSelectAll}
                  />
                );
              }
              
              // Regular column headers
              return (
                <ResizableTableHead
                  key={column.id}
                  column={column}
                  onResizeStart={onResizeStart}
                  onAutoFit={onAutoFit}
                  onKeyboardResize={onKeyboardResize}
                  isResizing={isResizing && resizingColumn === column.id}
                  sortDirection={sortConfig?.key === column.id ? sortConfig.direction : null}
                  onSort={column.sortable !== false ? onSort : undefined}
                >
                  {column.title}
                </ResizableTableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && orders.length === 0 ? (
            // Initial-load skeleton — prevents the empty-table flash that
            // surfaced after the Feb 2026 prod deploy (reported as
            // "Showing 0 to 0 of 0 orders" while the v2 endpoint was still
            // in flight). Renders 8 placeholder rows matching the column
            // count so the layout doesn't jump on data arrival.
            Array.from({ length: 8 }).map((_, idx) => (
              <TableRow
                key={`orders-skeleton-${idx}`}
                className={getDensityClasses()}
                style={{ borderBottom: '1px solid #e5e7eb' }}
                data-testid={`orders-table-skeleton-row-${idx}`}
              >
                {visibleColumns.map((column) => (
                  <td
                    key={`${column.id}-${idx}`}
                    className={getCellDensityClasses()}
                  >
                    <div
                      className="h-3 rounded bg-slate-200 animate-pulse"
                      style={{ width: `${50 + ((idx + column.id.length) % 35)}%` }}
                    />
                  </td>
                ))}
              </TableRow>
            ))
          ) : !loading && orders.length === 0 ? (
            // Empty-state — fired when the API resolves with zero orders
            // (search returns nothing, filters exclude everything, or
            // status tab has no matches). Renders ONCE inside a
            // full-width <td> spanning the visible columns so headers
            // and pagination stay in place. Replaces the previous blank
            // table that looked broken to users.
            <TableRow data-testid="orders-table-empty-state" className="hover:bg-transparent">
              <td colSpan={visibleColumns.length} className="py-16">
                <div className="flex flex-col items-center justify-center text-center px-6">
                  <div
                    className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mb-4"
                    aria-hidden="true"
                  >
                    {fetchError
                      ? <AlertTriangle className="h-8 w-8 text-amber-500" strokeWidth={1.5} />
                      : <Inbox className="h-8 w-8 text-slate-400" strokeWidth={1.5} />}
                  </div>
                  {fetchError ? (
                    <>
                      <p className="text-base font-medium text-slate-700" data-testid="orders-fetch-error-message">
                        We couldn&rsquo;t load orders
                      </p>
                      <p className="text-sm text-slate-500 mt-1 max-w-md">
                        The server didn&rsquo;t respond in time. Your data is safe — please retry.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={onRetry}
                        data-testid="orders-fetch-error-retry-btn"
                      >
                        Retry
                      </Button>
                    </>
                  ) : searchTerm ? (
                    <>
                      <p className="text-base font-medium text-slate-700" data-testid="orders-empty-search-message">
                        No orders found for &ldquo;{searchTerm}&rdquo;
                      </p>
                      <p className="text-sm text-slate-500 mt-1 max-w-md whitespace-normal" data-testid="orders-empty-search-hint">
                        Check the Order Number, Product ID, Client Code,
                        Borrower Name, Address or Assignee and try again. Completed and
                        cancelled orders only appear for an exact Order Number
                        or Product ID, or on their status tab.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-base font-medium text-slate-700">
                        There are no records to display
                      </p>
                      <p className="text-sm text-slate-500 mt-1 max-w-md">
                        Try adjusting your search keyword, status tab, or filters
                        to see more orders.
                      </p>
                    </>
                  )}
                </div>
              </td>
            </TableRow>
          ) : (
            orders.map((order) => (
            <TableRow 
              key={order.id} 
              className={`${getDensityClasses()} ${
                order.priority?.toLowerCase() === 'urgent' ? 'bg-red-50 border-l-4 border-l-red-500' :
                order.priority?.toLowerCase() === 'rush' ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
              }`}
              style={{ borderBottom: '1px solid #e5e7eb' }}
              data-testid={`order-row-${order.id}`}
            >
              {visibleColumns.map((column) => (
                <React.Fragment key={column.id}>
                  {renderCellContent({
                    columnId: column.id,
                    column,
                    order,
                    user,
                    selectedOrders,
                    selectedOrderIds,
                    availableProcessors,
                    onAdminSelectOrder,
                    onSelectOrder,
                    onOrderNumberClick,
                    canOpenOrderViaMatrix,
                    isOrderDeskView,
                    inMlsContext,
                    getCellDensityClasses,
                    formatPropertyAddress,
                    getBorrowerName,
                    getPropertyCounty,
                    getPropertyState
                  })}
                </React.Fragment>
              ))}
            </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <ResizeGuide isResizing={isResizing} />
    </>
  );
};

export default OrdersTable;
